/**
 * Tetrish - Main JS Entry Point
 * Initializes the game and coordinates all components
 */

// Import components
import { GameBoard } from './components/board.js';
import { TetrominoFactory, getWallKicks } from './components/pieces.js';
import { Controls } from './components/controls.js';
import { UI } from './components/ui.js';
import { ScoreManager } from './services/score-manager.js';
import { GameState } from './services/game-state.js';
import { SoundManager } from './services/sound-manager.js';
import { preloadAssets } from './utils/asset-loader.js';
import {
	INITIAL_DROP_INTERVAL,
	MIN_DROP_INTERVAL,
	DROP_INTERVAL_DECREMENT,
	LOCK_DELAY,
	MAX_LOCK_RESETS,
} from './constants.js';

// Main game class
class Game {
	constructor() {
		// Initialize services
		this.state = new GameState();
		this.scoreManager = new ScoreManager();
		this.soundManager = new SoundManager();

		// Initialize UI components
		this.ui = new UI(this.state, this.scoreManager);

		// Initialize game components
		this.board = new GameBoard(this.state);
		this.tetrominoFactory = new TetrominoFactory();
		this.controls = new Controls(this);

		// Game loop variables
		this.lastTime = 0;
		this.dropInterval = INITIAL_DROP_INTERVAL;
		this.dropCounter = 0;
		this.animationId = null;

		// Lock delay — piece must rest on ground this long before locking
		this.lockDelay = LOCK_DELAY;
		this.lockCounter = 0;    // accumulated ground-contact time (ms)
		this.lockResets = 0;     // times lock timer was reset for current piece
		this.maxLockResets = MAX_LOCK_RESETS;

		// Bind methods
		this.gameLoop = this.gameLoop.bind(this);
		this.handleGameOver = this.handleGameOver.bind(this);

		// Set up event listeners
		this._setupEventListeners();
	}

	/**
	 * Initialize the game
	 */
	async init() {
		try {
			// Preload assets
			await preloadAssets();

			// Generate the game board
			this.board.createBoard();

			// Initialize UI elements
			this.ui.initUI();

			// Make sure the game starts in the correct state
			this.state.reset();
			this.ui.hideOverlay(); // Ensure no overlay is shown on init

			console.log('Game initialized successfully');
		} catch (error) {
			console.error('Error initializing game:', error);
		}
	}

	/**
	 * Start a new game
	 */
	startGame() {
		// If game is already running, reset it
		if (this.state.isGameRunning) {
			this.state.reset();
		}

		// Reset the game state
		this.scoreManager.resetScore();
		this.board.clearBoard();

		// Hide any overlays that might be showing
		this.ui.hideOverlay();

		// Generate first piece
		this.state.currentPiece = this.tetrominoFactory.getRandomPiece();
		this.state.nextPiece = this.tetrominoFactory.getRandomPiece();

		// Update UI
		this.ui.updateNextPieceDisplay(this.state.nextPiece);
		this.ui.updateScore(this.scoreManager.score);
		this.ui.updateLevel(this.scoreManager.level);
		this.ui.updateLines(this.scoreManager.lines);

		// Start game loop
		this.state.isGameRunning = true;
		this.state.isPaused = false;
		this.lastTime = 0;
		this._resetLockState();

		this.soundManager.playSound('gameStart');

		// Start animation frame
		cancelAnimationFrame(this.animationId);
		this.animationId = requestAnimationFrame(this.gameLoop);

		// Update UI state
		this.ui.updateGameState(this.state);

		// notify sound manager that the game has begun
		window.dispatchEvent(new Event('game-started'));
	}

	/**
	 * Pause/resume the game
	 */
	togglePause() {
		// Only toggle pause if the game is actually running
		if (!this.state.isGameRunning) {
			console.log('Cannot pause: Game is not running');
			return;
		}

		this.state.isPaused = !this.state.isPaused;
		console.log('Game paused state toggled to:', this.state.isPaused);

		if (this.state.isPaused) {
			// Pause game
			cancelAnimationFrame(this.animationId);
			this.soundManager.playSound('pause');
			this.ui.showOverlay('Game Paused', 'Press "P" to resume');
		} else {
			// Resume game
			this.ui.hideOverlay();
			this.lastTime = 0;
			this.animationId = requestAnimationFrame(this.gameLoop);
			this.soundManager.playSound('resume');
		}

		// Update UI
		this.ui.updateGameState(this.state);
	}

	/**
	 * End the current game
	 */
	handleGameOver() {
		this.state.isGameRunning = false;
		cancelAnimationFrame(this.animationId);

		this.soundManager.playSound('gameOver');
		this.ui.showOverlay('Game Over', 'Press "Start Game" to play again');

		// Update high score if necessary
		if (this.scoreManager.score > this.scoreManager.highScore) {
			this.scoreManager.updateHighScore();
			this.ui.updateHighScore(this.scoreManager.highScore);
		}

		// notify sound manager that the game has ended
		window.dispatchEvent(new Event('game-ended'));
	}

	/**
	 * Move the current piece
	 */
	movePiece(dx, dy = 0) {
		if (!this.state.isGameRunning || this.state.isPaused) return;

		const currentPiece = this.state.currentPiece;
		currentPiece.move(dx, dy);

		// Check for collisions
		if (this.board.checkCollision(currentPiece)) {
			currentPiece.move(-dx, -dy); // Move back if collision
			return false;
		}

		this.soundManager.playSound('move');
		this._tryResetLockDelay();
		this.board.updateGhostPiece(this.state);
		this.board.render(this.state);
		return true;
	}

	/**
	 * Rotate the current piece using SRS wall kicks
	 */
	rotatePiece() {
		if (!this.state.isGameRunning || this.state.isPaused) return;

		const currentPiece = this.state.currentPiece;
		const prevRotation = currentPiece.rotation;

		currentPiece.rotate();
		const newRotation = currentPiece.rotation;

		// Try each SRS kick offset in order until one succeeds
		const kicks = getWallKicks(currentPiece.type, prevRotation, newRotation);
		let success = false;

		for (const [dx, dy] of kicks) {
			currentPiece.move(dx, dy);
			if (!this.board.checkCollision(currentPiece)) {
				success = true;
				break;
			}
			currentPiece.move(-dx, -dy); // revert this kick, try next
		}

		if (!success) {
			currentPiece.rotateBack();
			return false;
		}

		this.soundManager.playSound('rotate');
		this._tryResetLockDelay();
		this.board.updateGhostPiece(this.state);
		this.board.render(this.state);
		return true;
	}

	/**
	 * Drop the current piece immediately (hard drop)
	 */
	hardDrop() {
		if (!this.state.isGameRunning || this.state.isPaused) return;

		const currentPiece = this.state.currentPiece;
		let dropDistance = 0;

		// Move down until collision
		while (!this.board.checkCollision(currentPiece)) {
			currentPiece.move(0, 1);
			dropDistance++;
		}

		// Move back up one position (last valid position)
		currentPiece.move(0, -1);
		dropDistance--;

		// Add score for hard drop
		this.scoreManager.addDropScore(dropDistance);

		// Lock piece and generate next
		this.lockPieceAndContinue();

		this.soundManager.playSound('hardDrop');
	}

	/**
	 * Hold the current piece
	 */
	holdPiece() {
		if (!this.state.isGameRunning || this.state.isPaused || this.state.hasHeldPiece) return;

		const currentPiece = this.state.currentPiece;

		if (this.state.heldPiece) {
			// Swap with held piece
			const temp = this.state.heldPiece;
			this.state.heldPiece = currentPiece;
			this.state.currentPiece = temp;
			this.state.currentPiece.reset();
		} else {
			// Hold current piece and get next piece
			this.state.heldPiece = currentPiece;
			this.state.currentPiece = this.state.nextPiece;
			this.state.nextPiece = this.tetrominoFactory.getRandomPiece();
			this.ui.updateNextPieceDisplay(this.state.nextPiece);
		}

		this.state.hasHeldPiece = true;
		this.ui.updateHeldPieceDisplay(this.state.heldPiece);
		this.soundManager.playSound('hold');
	}

	/**
	 * Lock the current piece in place and continue with the next piece
	 */
	lockPieceAndContinue() {
		this._resetLockState(); // Reset lock timer for the incoming piece

		const piece = this.state.currentPiece;

		// Detect T-spin BEFORE locking (checks grid occupancy around the piece)
		const tspinResult = this.board.checkTspin(piece);
		const isTspin = tspinResult === 'tspin';
		const isTspinMini = tspinResult === 'tspin-mini';

		// Lock the current piece on the board
		const linesCleared = this.board.lockPiece(piece);

		// All-clear: every non-empty row is complete and will be cleared
		const isAllClear = linesCleared > 0 && this.board.isBoardClear();

		// Update score based on lines cleared (including T-spin / all-clear bonuses)
		if (linesCleared > 0 || isTspin || isTspinMini) {
			this.scoreManager.addLinesClearedScore(linesCleared, { isTspin, isTspinMini, isAllClear });
			this.ui.updateScore(this.scoreManager.score);
			this.ui.updateLines(this.scoreManager.lines);

			// Check for level up
			if (this.scoreManager.checkLevelUp()) {
				this.updateDifficulty();
				this.ui.updateLevel(this.scoreManager.level);
				this.soundManager.playSound('levelUp');
			} else if (linesCleared > 0) {
				this.soundManager.playSound(`clear${linesCleared}`);
			}
		}

		// Reset held piece flag
		this.state.hasHeldPiece = false;

		// Get the next piece
		this.state.currentPiece = this.state.nextPiece;
		this.state.nextPiece = this.tetrominoFactory.getRandomPiece();

		// Update UI
		this.ui.updateNextPieceDisplay(this.state.nextPiece);

		// Check for game over
		if (this.board.checkCollision(this.state.currentPiece)) {
			this.handleGameOver();
		}
	}

	/**
	 * Update game difficulty based on level
	 */
	updateDifficulty() {
		// Decrease drop interval as level increases
		this.dropInterval = Math.max(MIN_DROP_INTERVAL, INITIAL_DROP_INTERVAL - (this.scoreManager.level - 1) * DROP_INTERVAL_DECREMENT);
	}

	/**
	 * Returns true if the current piece is resting on the floor or a locked piece.
	 * Temporarily moves the piece to test — no side effects.
	 */
	_isPieceOnGround() {
		const piece = this.state.currentPiece;
		if (!piece) return false;
		piece.move(0, 1);
		const grounded = this.board.checkCollision(piece);
		piece.move(0, -1);
		return grounded;
	}

	/**
	 * Reset lock delay counters (called on piece spawn and hard drop).
	 */
	_resetLockState() {
		this.lockCounter = 0;
		this.lockResets = 0;
	}

	/**
	 * If the piece is grounded and resets remain, restart the lock timer.
	 * Called after every successful player move or rotation.
	 */
	_tryResetLockDelay() {
		if (this._isPieceOnGround() && this.lockResets < this.maxLockResets) {
			this.lockCounter = 0;
			this.lockResets++;
		}
	}

	/**
	 * Main game loop
	 */
	gameLoop(timestamp) {
		if (!this.state.isGameRunning || this.state.isPaused) return;

		const deltaTime = timestamp - this.lastTime;
		this.lastTime = timestamp;

		// Gravity
		this.dropCounter += deltaTime;
		if (this.dropCounter > this.dropInterval) {
			this.dropPiece();
		}

		// Lock delay — accumulate ground-contact time; lock when timer expires
		if (this._isPieceOnGround()) {
			this.lockCounter += deltaTime;
			if (this.lockCounter >= this.lockDelay) {
				this._resetLockState();
				this.lockPieceAndContinue();
				if (!this.state.isGameRunning) return; // game over
			}
		} else {
			this.lockCounter = 0;
		}

		// Show ghost piece
		this.board.updateGhostPiece(this.state);

		// Render current state
		this.board.render(this.state);

		// Continue loop
		this.animationId = requestAnimationFrame(this.gameLoop);
	}

	/**
	 * Drop the piece one row
	 */
	dropPiece() {
		if (!this.state.isGameRunning || this.state.isPaused) return;

		const currentPiece = this.state.currentPiece;

		// Try to move down
		currentPiece.move(0, 1);

		// On collision, move back — lock delay in game loop handles the actual lock
		if (this.board.checkCollision(currentPiece)) {
			currentPiece.move(0, -1);
		}

		// Reset drop counter
		this.dropCounter = 0;
	}

	/**
	 * Setup event listeners for UI elements
	 */
	_setupEventListeners() {
		// Start button
		document.getElementById('start-btn').addEventListener('click', () => {
			this.startGame();
		});

		// Pause button
		document.getElementById('pause-btn').addEventListener('click', () => {
			this.togglePause();
		});

		// Settings button
		document.getElementById('settings-btn').addEventListener('click', () => {
			this.ui.toggleSettingsModal();
		});

		// Settings save button
		document.getElementById('save-settings').addEventListener('click', () => {
			this.ui.saveSettings();
			this.ui.hideSettingsModal(); // Use the new method to properly hide the modal
		});

		// Modal close button
		document.querySelector('.close-btn').addEventListener('click', () => {
			this.ui.hideSettingsModal(); // Use the new method to properly hide the modal
		});

		// Overlay button
		document.getElementById('overlay-button').addEventListener('click', () => {
			console.log('Overlay button clicked, game state:', this.state.isGameRunning, this.state.isPaused);

			if (this.state.isPaused && this.state.isGameRunning) {
				this.togglePause(); // Resume the game if it's paused
			} else if (!this.state.isGameRunning) {
				this.startGame(); // Start a new game if not running
			}
		});

		// How to play button
		document.getElementById('how-to-play-btn').addEventListener('click', (e) => {
			const tooltip = document.getElementById('controls-tooltip');
			const isExpanded = e.currentTarget.getAttribute('aria-expanded') === 'true';

			e.currentTarget.setAttribute('aria-expanded', !isExpanded);
			tooltip.hidden = isExpanded;
		});

		// Accessibility toggle
		document.getElementById('accessibility-toggle').addEventListener('click', () => {
			document.documentElement.classList.toggle('high-contrast');
		});
	}
}

// Initialize the game when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
	const game = new Game();
	game.init();

	// Expose game to window for debugging
	window.game = game;
});
